import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  MapPin, ChevronRight, ExternalLink, Users, Landmark, Building2, Calendar,
  BookOpen, Star, Search, Loader2, Compass, Newspaper, TreePine, ArrowRight,
} from "lucide-react";
import { getPlace, getPlaceNav, getPlacePhoto, getPlaceReadiness } from "@/lib/api";
import { T, useLanguage } from "@/context/LanguageContext";
import { useTTS } from "@/context/TTSContext";
import { usePlaceContext } from "@/context/PlaceContext";
import { CommunityContributions } from "@/components/site/CommunityContributions";
import { BuildPageCTA, PhotoPulsePanel, ChatsPulsePanel } from "@/components/site/NetworkModules";
import { PlaceNav } from "@/components/site/PlaceNav";
import { PlaceFAQ } from "@/components/site/PlaceFAQ";
import { ModuleCard } from "@/components/site/ModuleCard";
import { UtilityBar } from "@/components/site/UtilityBar";
import { MapModal } from "@/components/site/MapModal";
import { DynIcon } from "@/components/site/DynIcon";
import { PlaceToday } from "@/components/site/PlaceToday";
import { IntentSwitcher } from "@/components/site/IntentSwitcher";
import { DiscoveryGrid } from "@/components/site/DiscoveryGrid";
import { LocalNews } from "@/components/site/LocalNews";
import { EcosystemRail } from "@/components/site/EcosystemRail";
import { FamilyTreeHub } from "@/components/site/FamilyTreeHub";
import { CivicModule } from "@/components/site/CivicModule";
import { TrustPanel } from "@/components/site/TrustPanel";
import { SourcesRegistry } from "@/components/site/SourcesRegistry";
import { QuickFacts } from "@/components/site/QuickFacts";
import { StateSymbols } from "@/components/site/StateSymbols";
import { PlaceAlerts } from "@/components/site/PlaceAlerts";
import { Seo } from "@/components/site/Seo";
import { canonicalUrl, breadcrumbJsonLd, truncate } from "@/lib/seo";

const SCHEMA_TYPE = {
  state: "State", federal_district: "AdministrativeArea", territory: "AdministrativeArea",
  county: "AdministrativeArea", city: "City", town: "City", village: "City", cdp: "City",
  neighborhood: "Place",
};

// Which movable modules appear (and in what order) for each visitor intent.
const INTENT_ORDER = {
  explore: ["discovery", "neighborhoods", "news", "community", "familytree", "ecosystem"],
  visit: ["discovery", "news", "community", "neighborhoods", "familytree", "ecosystem"],
  live: ["news", "community", "neighborhoods", "familytree", "discovery", "ecosystem"],
  move: ["neighborhoods", "news", "community", "familytree", "discovery", "ecosystem"],
  student: ["neighborhoods", "discovery", "news", "community", "ecosystem", "familytree"],
  business: ["news", "ecosystem", "neighborhoods", "discovery", "community", "familytree"],
};

const COVERAGE_LABELS = { identity: "Identity", population: "Population", history: "History", government: "Government", economy: "Economy", education: "Education", healthcare: "Healthcare", tourism: "Tourism", culture: "Culture", nature: "Nature", transportation: "Transportation" };
const REL_LABEL = { BORN_IN: "Born here", RAISED_IN: "Raised here", WORKED_IN: "Worked here", DIED_IN: "Died here" };

// Bespoke state themes (character-driven templates)
const STATE_THEMES = {
  california: { grad: "from-[#c2410c] via-[#c8102e] to-[#0a2a5e]", tag: "Golden State sunsets & Pacific dreams" },
  texas: { grad: "from-[#92400e] via-[#a10d25] to-[#0a2a5e]", tag: "Bigger in the Lone Star State" },
  florida: { grad: "from-[#0e7490] via-[#0a7ea4] to-[#0a2a5e]", tag: "Sunshine, coasts & the Everglades" },
  hawaii: { grad: "from-[#047857] via-[#0a7ea4] to-[#0a2a5e]", tag: "Islands of the Aloha State" },
  "new-york": { grad: "from-[#1e293b] via-[#0a2a5e] to-[#111827]", tag: "From Manhattan to the Adirondacks" },
  washington: { grad: "from-[#065f46] via-[#0e7490] to-[#0a2a5e]", tag: "Evergreen coasts & Cascade volcanoes" },
  colorado: { grad: "from-[#1e40af] via-[#0e7490] to-[#0a2a5e]", tag: "Mile-high peaks of the Rockies" },
  arizona: { grad: "from-[#c2410c] via-[#b45309] to-[#7c2d12]", tag: "Red rock & Sonoran sun" },
  illinois: { grad: "from-[#0a2a5e] via-[#153a7d] to-[#111827]", tag: "Prairie skyline on Lake Michigan" },
  nevada: { grad: "from-[#a16207] via-[#c8102e] to-[#0a2a5e]", tag: "Neon nights & Silver State desert" },
  louisiana: { grad: "from-[#7e22ce] via-[#a16207] to-[#047857]", tag: "Jazz, bayous & Creole soul" },
  alaska: { grad: "from-[#0e7490] via-[#155e75] to-[#0a2a5e]", tag: "The Last Frontier — glaciers & aurora" },
  tennessee: { grad: "from-[#b45309] via-[#a10d25] to-[#0a2a5e]", tag: "Music City & the Great Smokies" },
  oregon: { grad: "from-[#047857] via-[#0e7490] to-[#0a2a5e]", tag: "Pacific coast to Cascade peaks" },
  pennsylvania: { grad: "from-[#a16207] via-[#a10d25] to-[#0a2a5e]", tag: "Where the nation was founded" },
  georgia: { grad: "from-[#ea580c] via-[#c8102e] to-[#0a2a5e]", tag: "Peach State & the New South" },
  michigan: { grad: "from-[#0e7490] via-[#155e75] to-[#0a2a5e]", tag: "Great Lakes & the Motor City" },
  massachusetts: { grad: "from-[#7f1d1d] via-[#a10d25] to-[#0a2a5e]", tag: "Birthplace of the Revolution" },
  virginia: { grad: "from-[#166534] via-[#0e7490] to-[#0a2a5e]", tag: "Old Dominion — where America began" },
  utah: { grad: "from-[#c2410c] via-[#9a3412] to-[#7c2d12]", tag: "Mighty red-rock canyons" },
  "new-mexico": { grad: "from-[#b45309] via-[#c2410c] to-[#7c2d12]", tag: "Land of Enchantment" },
  "north-carolina": { grad: "from-[#0e7490] via-[#0a2a5e] to-[#111827]", tag: "Blue Ridge to the Outer Banks" },
  minnesota: { grad: "from-[#0e7490] via-[#155e75] to-[#0a2a5e]", tag: "Land of 10,000 Lakes" },
  missouri: { grad: "from-[#a16207] via-[#a10d25] to-[#0a2a5e]", tag: "Gateway to the West" },
  wisconsin: { grad: "from-[#166534] via-[#0e7490] to-[#0a2a5e]", tag: "America's Dairyland" },
  "district-of-columbia": { grad: "from-[#7f1d1d] via-[#0a2a5e] to-[#111827]", tag: "The Nation's Capital" },
  "puerto-rico": { grad: "from-[#0a7ea4] via-[#c8102e] to-[#0a2a5e]", tag: "Isla del Encanto" },
  alabama: { grad: "from-[#7f1d1d] via-[#a10d25] to-[#0a2a5e]", tag: "Heart of Dixie — from Selma to the stars" },
  arkansas: { grad: "from-[#166534] via-[#0e7490] to-[#0a2a5e]", tag: "The Natural State — diamonds & hot springs" },
  connecticut: { grad: "from-[#1e3a8a] via-[#0a2a5e] to-[#111827]", tag: "The Constitution State on the Sound" },
  delaware: { grad: "from-[#a16207] via-[#0a2a5e] to-[#111827]", tag: "The First State" },
  idaho: { grad: "from-[#166534] via-[#155e75] to-[#0a2a5e]", tag: "Gem State rivers & Sawtooth peaks" },
  indiana: { grad: "from-[#a10d25] via-[#7f1d1d] to-[#0a2a5e]", tag: "Crossroads of America" },
  iowa: { grad: "from-[#166534] via-[#a16207] to-[#0a2a5e]", tag: "Fields of opportunity" },
  kansas: { grad: "from-[#b45309] via-[#a16207] to-[#0a2a5e]", tag: "Sunflowers on the Great Plains" },
  kentucky: { grad: "from-[#166534] via-[#0e7490] to-[#0a2a5e]", tag: "Bluegrass, bourbon & thoroughbreds" },
  maine: { grad: "from-[#0e7490] via-[#155e75] to-[#0a2a5e]", tag: "The way life should be" },
  maryland: { grad: "from-[#a16207] via-[#a10d25] to-[#0a2a5e]", tag: "Chesapeake shores & colonial roots" },
  mississippi: { grad: "from-[#7e22ce] via-[#a10d25] to-[#0a2a5e]", tag: "Birthplace of the blues" },
  montana: { grad: "from-[#155e75] via-[#0e7490] to-[#0a2a5e]", tag: "Big Sky Country" },
  nebraska: { grad: "from-[#a16207] via-[#166534] to-[#0a2a5e]", tag: "The good life on the plains" },
  "new-hampshire": { grad: "from-[#334155] via-[#0e7490] to-[#0a2a5e]", tag: "Live free or die" },
  "new-jersey": { grad: "from-[#a16207] via-[#0a7ea4] to-[#0a2a5e]", tag: "The Garden State shore" },
  "north-dakota": { grad: "from-[#166534] via-[#a16207] to-[#0a2a5e]", tag: "Legendary badlands & prairie" },
  ohio: { grad: "from-[#7f1d1d] via-[#0a2a5e] to-[#111827]", tag: "The heart of it all" },
  oklahoma: { grad: "from-[#c2410c] via-[#b45309] to-[#7c2d12]", tag: "Native heritage on Route 66" },
  "rhode-island": { grad: "from-[#0e7490] via-[#0a7ea4] to-[#0a2a5e]", tag: "The Ocean State" },
  "south-carolina": { grad: "from-[#7e22ce] via-[#a10d25] to-[#0a2a5e]", tag: "Palmetto coasts & Lowcountry charm" },
  "south-dakota": { grad: "from-[#b45309] via-[#a16207] to-[#0a2a5e]", tag: "Great faces, great places" },
  vermont: { grad: "from-[#166534] via-[#15803d] to-[#0a2a5e]", tag: "The Green Mountain State" },
  "west-virginia": { grad: "from-[#166534] via-[#155e75] to-[#0a2a5e]", tag: "Wild & wonderful Appalachia" },
  wyoming: { grad: "from-[#b45309] via-[#166534] to-[#0a2a5e]", tag: "Forever West — Yellowstone & the Tetons" },
  guam: { grad: "from-[#0e7490] via-[#0a7ea4] to-[#0a2a5e]", tag: "Where America's day begins" },
  "us-virgin-islands": { grad: "from-[#0891b2] via-[#0a7ea4] to-[#0a2a5e]", tag: "America's Caribbean" },
  "american-samoa": { grad: "from-[#166534] via-[#0e7490] to-[#0a2a5e]", tag: "Motu o Fiafiaga — islands of joy" },
  "northern-mariana-islands": { grad: "from-[#0891b2] via-[#155e75] to-[#0a2a5e]", tag: "The Marianas — Pacific crossroads" },
};

export default function PlaceProfile() {
  const loc = useLocation();
  const path = loc.pathname.replace(/^\/place/, "");
  const [data, setData] = useState(null);
  const [nav, setNav] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [intent, setIntent] = useState("explore");
  const { lang, dict } = useLanguage();
  const tts = useTTS();
  const placeCtx = usePlaceContext();

  useEffect(() => {
    setData(null); setNav(null); setReadiness(null); setPhoto(null); setError(false);
    window.scrollTo(0, 0);
    getPlace(path).then((d) => {
      setData(d);
      placeCtx?.setCurrent({ geo_id: d.geo_id, name: d.official_name });
      getPlaceNav(d.geo_id).then(setNav).catch(() => {});
      getPlaceReadiness(d.geo_id).then(setReadiness).catch(() => {});
      // Contextual, attributed hero photo (per-geo cached) when no seeded image
      if (!d.image_url) {
        getPlacePhoto(d.geo_id).then((r) => setPhoto(r.photo)).catch(() => {});
      }
    }).catch(() => setError(true));
  }, [path]); // eslint-disable-line

  useEffect(() => () => placeCtx?.setCurrent(null), []); // eslint-disable-line

  const snippets = useMemo(
    () => (nav?.modules || []).filter((m) => m.component === "article" && m.snippet).map((m) => m.snippet),
    [nav]
  );

  useEffect(() => {
    if (!data || !tts) return;
    const src = [data.description, ...snippets].filter(Boolean);
    const readable = lang.code === "en" ? src : src.map((t) => dict[t] || t);
    tts.setContent(readable);
  }, [data, snippets, lang.code, dict, tts]);

  if (error) return <div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-3xl text-navy"><T>Place not found</T></h1><Link to="/explore/states" className="mt-4 inline-block text-oldglory-red"><T>Back to Explore</T></Link></div>;
  if (!data) return (
    <div className="mx-auto flex max-w-[1500px] flex-col items-center px-4 py-28 text-center" data-testid="place-loading">
      <Loader2 className="h-10 w-10 animate-spin text-oldglory-red" />
      <p className="mt-5 font-display text-xl font-bold text-navy"><T>Fetching & processing this place…</T></p>
      <p className="mt-1 text-sm text-muted-foreground"><T>Pulling official data and preparing articles. This only takes a moment.</T></p>
      <div className="mt-8 w-full max-w-[1200px] space-y-4">
        <div className="h-40 animate-pulse rounded-xl bg-parchment" />
        <div className="grid gap-4 sm:grid-cols-3"><div className="h-24 animate-pulse rounded-xl bg-parchment" /><div className="h-24 animate-pulse rounded-xl bg-parchment" /><div className="h-24 animate-pulse rounded-xl bg-parchment" /></div>
      </div>
    </div>
  );

  const readableAll = [data.description, ...snippets].filter(Boolean);
  const series = (data.population_series || []).map(([year, value]) => ({ year: String(year), value }));
  const typeLabel = data.geo_type.replace("_", " ");
  const cov = data.coverage || { detail: {}, overall: 0 };
  const theme = STATE_THEMES[data.geo_id];
  const kids = data.children || [];
  const childCounties = kids.filter((c) => c.geo_type === "county");
  const childCities = kids.filter((c) => ["city", "town", "village", "cdp"].includes(c.geo_type));
  const childNeighborhoods = kids.filter((c) => c.geo_type === "neighborhood");
  const readableOf = (txt) => (lang.code === "en" ? txt : (dict[txt] || txt));
  const isReading = (txt) => !!tts?.activeText && tts.activeText === readableOf(txt);
  const HL = "rounded-md bg-oldglory-red/10 shadow-[inset_3px_0_0_0_#c8102e] px-2 -mx-2 transition-colors duration-300";
  const density = data.population && data.area_sq_mi ? Math.round(data.population / data.area_sq_mi) : null;

  const tabs = nav?.tabs || [];
  const modulesByTab = {};
  (nav?.modules || []).forEach((m) => { (modulesByTab[m.tab] = modulesByTab[m.tab] || []).push(m); });

  const jumpTo = (tabKey) => {
    const el = document.getElementById(`tab-${tabKey}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollBlk = (k) => {
    const el = document.getElementById(`blk-${k}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Discovery cards from article modules that actually have content.
  const discoveryItems = (nav?.modules || [])
    .filter((m) => m.component === "article" && m.has_content && m.key !== "about")
    .slice(0, 6);

  const identityStatement = theme?.tag || (data.known_for || [])[0] || null;

  // Movable "living portal" blocks, reordered by visitor intent.
  const movable = {
    discovery: () => <DiscoveryGrid key="discovery" items={discoveryItems} onJump={jumpTo} />,
    neighborhoods: () => ((childCounties.length || childCities.length || childNeighborhoods.length)
      ? <NeighborhoodsPreview key="neighborhoods" counties={childCounties} cities={childCities.length ? childCities : childNeighborhoods} onJump={() => jumpTo("explore")} />
      : null),
    news: () => <LocalNews key="news" geoId={data.geo_id} placeName={data.official_name} />,
    community: () => <CommunityContributions key="community" geoId={data.geo_id} placeName={data.official_name} />,
    familytree: () => <FamilyTreeHub key="familytree" placeName={data.official_name} />,
    ecosystem: () => <EcosystemRail key="ecosystem" placeName={data.official_name} />,
  };
  const order = INTENT_ORDER[intent] || INTENT_ORDER.explore;
  const navTabs = [...tabs, { key: "family-tree", label: "Family", icon: "tree-pine", target: "blk-familytree", accent: true }];
  const todayChips = [
    { id: "explore", label: "Explore", icon: <Compass className="h-3.5 w-3.5" />, onClick: () => scrollBlk("discovery") },
    { id: "news", label: "Local News", icon: <Newspaper className="h-3.5 w-3.5" />, onClick: () => scrollBlk("news") },
    { id: "familytree", label: "Family Tree", icon: <TreePine className="h-3.5 w-3.5" />, onClick: () => scrollBlk("familytree") },
  ];

  const renderModule = (m) => {
    switch (m.component) {
      case "about":
        return (
          <section key="about" data-testid="module-about">
            <SectionTitle icon={BookOpen} title={`About ${data.official_name}`} />
            <p className={`text-lg leading-relaxed text-ink/85 ${isReading(data.description) ? HL : ""}`} data-testid="about-paragraph"><T>{data.description}</T></p>
            {data.wikipedia?.available && (
              <a href={data.wikipedia.wikipedia_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-parchment/50 px-4 py-2.5 text-sm font-medium text-navy transition-colors hover:border-navy/40" data-testid="wikipedia-link">
                <BookOpen className="h-4 w-4 text-oldglory-red" /> <T>Learn more on Wikipedia</T> <ExternalLink className="h-3.5 w-3.5 opacity-60" />
              </a>
            )}
            {data.known_for?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2.5" data-testid="known-for">
                {data.known_for.map((k) => (
                  <span key={k} className="rounded-full bg-navy/5 px-4 py-2 text-sm font-medium text-navy ring-1 ring-navy/10"><T>{k}</T></span>
                ))}
              </div>
            )}
            <AtAGlance data={data} density={density} cov={cov} />
          </section>
        );
      case "population":
        return (
          <section key="population" data-testid="module-population">
            <SectionTitle icon={Users} title="Population & Demographics" />
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {data.population != null && <Stat label="Population" value={data.population.toLocaleString()} />}
              {density != null && <Stat label="Density / sq mi" value={density.toLocaleString()} />}
              {data.area_sq_mi != null && <Stat label="Land area" value={`${data.area_sq_mi.toLocaleString()} sq mi`} />}
              {data.admitted && <Stat label="Established" value={data.admitted} />}
            </div>
            {series.length > 1 && (
              <div className="rounded-xl border border-border bg-white p-5">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : `${(v / 1e3).toFixed(0)}K`)} />
                    <Tooltip formatter={(v) => v.toLocaleString()} />
                    <Line type="monotone" dataKey="value" stroke="#c8102e" strokeWidth={3} dot={{ r: 4, fill: "#0a2a5e" }} />
                  </LineChart>
                </ResponsiveContainer>
                <p className="mt-2 text-xs text-muted-foreground">Source: U.S. Census Bureau</p>
              </div>
            )}
          </section>
        );
      case "places":
        return (
          <div key="places">
            {childCounties.length > 0 && <PlaceGrid title={`Counties in ${data.official_name}`} icon={Building2} items={childCounties} testid="counties-browser" countLabel="counties" />}
            {childCities.length > 0 && <PlaceGrid title={`Major Cities & Towns in ${data.official_name}`} icon={MapPin} items={childCities} testid="cities-browser" countLabel="places" allUrl={["state", "territory", "federal_district"].includes(data.geo_type) ? `/explore/${data.geo_id}/cities` : null} />}
          </div>
        );
      case "people":
        return (
          <section key="people" data-testid="module-people">
            <SectionTitle icon={Users} title={`Famous People from ${data.official_name}`} />
            {data.notable_people?.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="notable-people-card">
                {data.notable_people.map((p) => (
                  <Link key={p.id} to={`/people/${p.id}`} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-white transition-all hover:-translate-y-1 hover:border-navy/30 hover:shadow-lg" data-testid={`place-person-${p.id}`}>
                    {p.wiki_thumb ? (
                      <img src={p.wiki_thumb} alt={p.name} loading="lazy" className="h-32 w-full object-cover object-top" />
                    ) : (
                      <span className="flex h-32 w-full items-center justify-center bg-navy font-display text-2xl font-black text-white">{p.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}</span>
                    )}
                    <div className="flex flex-1 flex-col p-3">
                      <span className="font-display text-sm font-bold leading-tight text-navy group-hover:text-oldglory-red">{p.name}</span>
                      {p.wiki_desc && <span className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{p.wiki_desc}</span>}
                      <span className="mt-auto pt-2 text-[10px] font-semibold uppercase tracking-wider text-oldglory-red"><T>{REL_LABEL[p.relation] || ""}</T>{p.from_name ? ` · ${p.from_name}` : ""}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground"><T>Notable people for this place are being added.</T></p>
            )}
          </section>
        );
      case "stories":
        return null; // Community Voices now render in the intent-ordered living-portal region
      case "symbols":
        return <StateSymbols key="symbols" geoId={data.geo_id} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Seo
        title={data.official_name}
        description={data.description || `Explore ${data.official_name} on USAPulse — people, history, culture, economy, government, things to do and the local pulse right now.`}
        path={loc.pathname}
        type="article"
        noindex={readiness ? !readiness.indexable : false}
        image={data.image_url || photo?.image_url}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": SCHEMA_TYPE[data.geo_type] || "Place",
            name: data.official_name,
            description: truncate(data.description, 300) || undefined,
            url: canonicalUrl(loc.pathname),
            image: data.image_url || photo?.image_url || undefined,
            ...(data.latitude && data.longitude
              ? { geo: { "@type": "GeoCoordinates", latitude: data.latitude, longitude: data.longitude } }
              : {}),
          },
          breadcrumbJsonLd((data.breadcrumb || []).map((b) => ({ name: b.name, url_path: `/place${b.url_path}` }))),
        ]}
      />
      {/* Compact hero */}
      <div className={`relative overflow-hidden text-white ${theme ? "bg-gradient-to-br " + theme.grad : "bg-navy"}`}>
        {(data.image_url || photo?.image_url) && (
          <div className="absolute inset-0">
            <img src={data.image_url || photo.image_url} alt={photo?.alt || data.official_name} className="h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
          </div>
        )}
        <div className="relative mx-auto max-w-[1500px] px-4 py-8 md:py-10">
          <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-white/70 sm:text-sm" data-testid="breadcrumb">
            {data.breadcrumb.map((b, i) => (
              <span key={b.geo_id} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
                {i === data.breadcrumb.length - 1 ? (
                  <span className="font-semibold text-white">{b.name}</span>
                ) : (
                  <Link to={i === 0 ? "/explore/states" : `/place${b.url_path}`} className="hover:text-white">{b.name}</Link>
                )}
              </span>
            ))}
          </nav>
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_360px]">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-2 inline-block rounded bg-oldglory-red px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">{typeLabel}</span>
              <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">{data.official_name}</h1>
              {identityStatement && <p className="mt-2 max-w-2xl font-display text-lg italic text-white/80 sm:text-xl"><T>{identityStatement}</T></p>}
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/80">
                {data.capital && <Fact icon={Landmark} label="Capital / Seat" value={data.capital} />}
                {data.population != null && <Fact icon={Users} label="Population" value={data.population.toLocaleString()} />}
                {data.region && <Fact icon={MapPin} label="Region" value={data.region} />}
                {data.admitted && <Fact icon={Calendar} label="Since" value={data.admitted} />}
              </div>
            </motion.div>
            <div className="lg:justify-self-end lg:w-[360px]">
              <PlaceToday geoId={data.geo_id} chips={todayChips} variant="hero" />
            </div>
          </div>
        </div>
        {photo && !data.image_url && (
          <div className="absolute bottom-1.5 left-3 z-10 text-[10px] text-white/60" data-testid="unsplash-attribution">
            <T>Photo by</T>{" "}
            <a href={photo.photographer_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">{photo.photographer}</a>{" "}
            <T>on</T>{" "}
            <a href={photo.unsplash_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Unsplash</a>
          </div>
        )}
      </div>

      {/* Utility bar: Listen · Translate · Map · Sources */}
      <div className="border-b border-border bg-parchment/60">
        <div className="mx-auto max-w-[1500px] px-4 py-3">
          <UtilityBar paragraphs={readableAll} wikipedia={data.wikipedia} onMap={() => setMapOpen(true)} sharePath={data.url_path ? `place${data.url_path}` : null} />
        </div>
      </div>

      {/* Official NWS active alerts (high visibility, top of page) */}
      <PlaceAlerts geoId={data.geo_id} />

      {/* Source-governed Quick Facts (Census PEP/ACS + Census geography) */}
      <QuickFacts geoId={data.geo_id} />

      {/* Standardized sub-nav (sticky) — Explore/Visit/… + Family */}
      {navTabs.length > 1 && <PlaceNav tabs={navTabs} />}

      {/* Living portal — intent switcher + intent-ordered discovery modules */}
      <div className="mx-auto max-w-[1200px] space-y-10 px-4 py-8">
        {readiness && !readiness.indexable && (
          <BuildPageCTA placeName={data.official_name} onContribute={() => scrollBlk("community")} />
        )}
        <div className="rounded-2xl border border-border bg-white p-4 sm:p-5">
          <IntentSwitcher active={intent} onChange={setIntent} />
        </div>
        {order.map((key) => {
          const el = movable[key]?.();
          return el ? <div key={key} id={`blk-${key}`} className="scroll-mt-24">{el}</div> : null;
        })}
        <PhotoPulsePanel data={data} />
        <ChatsPulsePanel data={data} />
      </div>

      {/* Explore everything — deep standardized tab content */}
      <div className="border-t border-border bg-parchment/40">
        <div className="mx-auto max-w-[1200px] px-4 pt-10">
          <h2 className="font-display text-2xl font-black tracking-tight text-navy sm:text-3xl"><T>Explore everything about</T> {data.official_name}</h2>
        </div>
        <div className="mx-auto max-w-[1200px] space-y-16 px-4 py-10">
          {tabs.map((tab) => {
            const mods = modulesByTab[tab.key] || [];
            if (!mods.length) return null;
            const articleMods = mods.filter((m) => m.component === "article" && m.has_content);
            const compMods = mods.filter((m) => m.component !== "article");
            if (!compMods.length && !articleMods.length && tab.key !== "work") return null;
            return (
              <section key={tab.key} id={`tab-${tab.key}`} className="scroll-mt-24" data-testid={`section-${tab.key}`}>
                <div className="mb-6 flex items-center gap-3 border-b border-border pb-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-white">
                    <DynIcon name={tab.icon} className="h-5 w-5" />
                  </span>
                  <h2 className="font-display text-3xl font-black text-navy"><T>{tab.label}</T></h2>
                </div>
                <div className="space-y-10">
                  {tab.key === "government" && <CivicModule geoId={data.geo_id} />}
                  {tab.key === "work" && (
                    <Link to={`/homes/place/${data.geo_id}`} className="group flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-[#2563EB]/20 bg-[#EEF2FF] p-5 transition-colors hover:border-[#2563EB]" data-testid="homespulse-cta">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-widest text-[#2563EB]">HomesPulse.pro</span>
                        <h3 className="mt-0.5 font-display text-xl font-black text-[#0F172A]"><T>Real estate in</T> {data.official_name}</h3>
                        <p className="text-sm text-slate-600"><T>Homes for sale & rent, market data and local agents — with full community intelligence.</T></p>
                      </div>
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 font-bold text-white transition-colors group-hover:bg-[#1D4ED8]"><T>Explore homes</T> <ChevronRight className="h-4 w-4" /></span>
                    </Link>
                  )}
                  {compMods.map((m) => renderModule(m))}
                  {articleMods.length > 0 && (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`articles-${tab.key}`}>
                      {articleMods.map((m) => <ModuleCard key={m.key} geoId={data.geo_id} module={m} />)}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
          {tabs.length === 0 && <div className="h-32 animate-pulse rounded-xl bg-parchment" />}
        </div>
      </div>

      {/* People Also Ask — AI-refined from visitor questions */}
      <PlaceFAQ geoId={data.geo_id} placeName={data.official_name} />
      <SourcesRegistry />

      {/* Data & Sources trust panel + Correct this page */}
      <TrustPanel geoId={data.geo_id} />

      {mapOpen && <MapModal place={data} onClose={() => setMapOpen(false)} />}
      {tts?.status && tts.status !== "idle" && <div className="h-28" aria-hidden data-testid="tts-spacer" />}
    </div>
  );
}

function NeighborhoodsPreview({ counties, cities, onJump }) {
  const items = (cities.length ? cities : counties).slice(0, 8);
  const label = cities.length ? "Neighborhoods & Nearby Places" : "Counties & Areas";
  return (
    <section data-testid="neighborhoods-preview">
      <div className="mb-4 flex items-end justify-between gap-3 border-b border-border pb-3">
        <h2 className="flex items-center gap-2.5 font-display text-2xl font-black tracking-tight text-navy sm:text-3xl">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy text-white"><MapPin className="h-5 w-5" /></span>
          <T>{label}</T>
        </h2>
        <button onClick={onJump} className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="neighborhoods-see-all">
          <T>See all</T> <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="neighborhoods-grid">
        {items.map((c) => (
          <Link key={c.geo_id} to={`/place${c.url_path}`}
            className="group flex flex-col justify-between gap-2 rounded-xl border border-border bg-white p-3.5 transition-all hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-md"
            data-testid={`neighborhood-${c.geo_id}`}>
            <span className="font-display text-sm font-bold leading-tight text-navy group-hover:text-oldglory-red">{c.name}</span>
            {c.population != null && <span className="text-[11px] tabular-nums text-muted-foreground">{c.population.toLocaleString()} <T>residents</T></span>}
          </Link>
        ))}
      </div>
    </section>
  );
}

function AtAGlance({ data, density, cov }) {
  return (
    <div className="mt-6 rounded-xl border border-border bg-white p-5" data-testid="identity-card">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-navy"><T>At a glance</T></span>
        <span className="text-xs font-semibold text-oldglory-red">{cov.overall}% <T>coverage</T></span>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-3">
        {data.state_code && <Row k="State Code" v={data.state_code} />}
        {(() => {
          const f = typeof data.fips === "string" ? null : (data.fips || data.fips_crosswalk);
          if (typeof data.fips === "string") return <Row k="FIPS" v={data.fips} />;
          if (f && (f.state || f.county || f.place))
            return <Row k="FIPS" v={[f.state, f.county, f.place].filter(Boolean).join("-")} />;
          return null;
        })()}
        {data.county_code && <Row k="County FIPS" v={data.county_code} />}
        {density != null && <Row k="Density" v={`${density.toLocaleString()}/sq mi`} />}
        {data.latitude != null && <Row k="Coordinates" v={`${data.latitude.toFixed(3)}, ${data.longitude.toFixed(3)}`} />}
        {data.area_sq_mi != null && <Row k="Area" v={`${data.area_sq_mi.toLocaleString()} sq mi`} />}
        {data.wikipedia?.wikidata_qid && <Row k="Wikidata" v={data.wikipedia.wikidata_qid} />}
      </dl>
      <p className="mt-4 border-t border-border pt-3 text-[11px] text-muted-foreground"><T>Last updated</T>: {data.last_updated} · <T>Source</T>: U.S. Census Bureau</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3.5">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><T>{label}</T></div>
      <div className="mt-1 font-display text-xl font-black text-navy">{value}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <h2 className="mb-3 flex items-center gap-2.5 font-display text-2xl font-bold text-navy">
      <Icon className="h-6 w-6 text-oldglory-red" strokeWidth={1.6} />
      <T>{title}</T>
    </h2>
  );
}
function Fact({ icon: Icon, label, value }) {
  return (
    <span className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-oldglory-red" />
      <span><span className="block text-[10px] uppercase tracking-wider text-white/50"><T>{label}</T></span><span className="font-semibold text-white">{value}</span></span>
    </span>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between gap-4"><dt className="text-muted-foreground"><T>{k}</T></dt><dd className="font-medium text-ink">{v}</dd></div>;
}

function PlaceGrid({ title, icon: Icon, items, testid, countLabel, allUrl }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? items.filter((c) => c.name.toLowerCase().includes(s)) : items;
    return [...list].sort((a, b) => (b.population || 0) - (a.population || 0));
  }, [q, items]);
  return (
    <section data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <SectionTitle icon={Icon} title={title} />
        <span className="rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy" data-testid={`${testid}-count`}>
          {items.length} <T>{countLabel}</T>
        </span>
      </div>
      {allUrl && (
        <Link to={allUrl} className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid={`${testid}-all-link`}>
          <T>Browse all cities & towns</T> <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
      {items.length > 12 && (
        <div className="relative mb-4 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Filter ${items.length} ${countLabel}…`}
            className="w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-navy" data-testid={`${testid}-filter`} />
        </div>
      )}
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid={`${testid}-grid`}>
        {filtered.map((c) => (
          <Link key={c.geo_id} to={`/place${c.url_path}`}
            className="group flex items-center justify-between gap-2 rounded-lg border border-transparent px-3 py-2 text-sm transition-colors hover:border-border hover:bg-parchment"
            data-testid={`child-${c.geo_id}`}>
            <span className="flex items-center gap-2 truncate text-ink">
              <Icon className="h-3.5 w-3.5 shrink-0 text-oldglory-red/70" />
              <span className="truncate group-hover:text-navy">{c.name}</span>
            </span>
            {c.population != null && <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{c.population.toLocaleString()}</span>}
          </Link>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}><T>No matches</T></p>}
    </section>
  );
}
